import { Component, inject, Input } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { TranslateModule } from '@ngx-translate/core'
import { lastValueFrom } from 'rxjs'
import { UploadsService } from '../uploads.service'
import { ToastService } from '../toast.service'
import { LoggerService } from '../logger.service'
import { FilesizePipe } from '../filesize.pipe'
import { CallsService } from '../calls.service'
import { getFileExtension, getFileName } from '../utils'
import { FileValidationService } from '../file-validation.service'

type UploadStatus = 'pending' | 'uploading' | 'completed' | 'failed'

export type FileToUpload = {
  id: string
  fileName: string
  extension: string
  uploadKey: string | null
  status: UploadStatus
  file: File
}

@Component({
  selector: 'app-multi-file-upload',
  standalone: true,
  imports: [CommonModule, MatButton, MatIconButton, MatIcon, TranslateModule, FilesizePipe],
  templateUrl: './multi-file-upload.component.html',
  styleUrl: './multi-file-upload.component.scss',
})
export class MultiFileUploadComponent {
  @Input({ required: true })
  acceptedFileTypes!: string[]

  @Input({ required: true })
  maxSize!: number

  @Input({ required: true })
  callId!: number

  uploadsService = inject(UploadsService)
  callsService = inject(CallsService)
  toastService = inject(ToastService)
  loggerService = inject(LoggerService)
  fileValidationService = inject(FileValidationService)
  importResourceMutation = this.callsService.importResource()

  fileQueue: FileToUpload[] = []
  isDragging = false
  isProcessing = false

  onDragOver(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = true
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = false
  }

  onDrop(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = false

    const files = event.dataTransfer?.files
    if (files) {
      this.handleFiles(Array.from(files))
    }
  }

  onFileInputChange(event: Event) {
    const input = event.target as HTMLInputElement
    if (input.files) {
      this.handleFiles(Array.from(input.files))
      input.value = ''
    }
  }

  handleFiles(files: File[]) {
    const validFiles = this.fileValidationService.validateFiles(files, this.acceptedFileTypes, this.maxSize)
    validFiles.forEach((file) => this.addFileToQueue(file))
    if (!this.isProcessing) {
      this.processQueue()
    }
  }

  addFileToQueue(file: File) {
    const fileId = `${Date.now()}-${Math.random()}`
    const extension = getFileExtension(file)
    const fileName = getFileName(file)
    const fileToUpload: FileToUpload = {
      id: fileId,
      fileName,
      extension: extension.toUpperCase(),
      uploadKey: null,
      status: 'pending',
      file,
    }
    this.fileQueue.push(fileToUpload)
  }

  async processQueue() {
    this.isProcessing = true
    while (this.fileQueue.length > 0) {
      const fileToUpload = this.fileQueue[0]
      if (fileToUpload.status === 'pending') {
        await this.uploadFile(fileToUpload)
      }
      this.fileQueue.shift()
    }
    this.isProcessing = false
  }

  async uploadFile(fileToUpload: FileToUpload): Promise<void> {
    fileToUpload.status = 'uploading'
    try {
      const uploadInfo = await lastValueFrom(this.uploadsService.upload('custom-document', fileToUpload.file))
      if (uploadInfo?.uploadKey === null || uploadInfo?.uploadKey === undefined) {
        throw new Error('Upload key not received for file upload with name ' + fileToUpload.fileName)
      }
      fileToUpload.uploadKey = uploadInfo.uploadKey
      await this.importResourceMutation.mutateAsync({
        callId: this.callId,
        data: {
          uploadKey: uploadInfo.uploadKey,
          fileExtension: fileToUpload.extension,
          fileType: 'CUSTOM_DOCUMENT',
          fileName: fileToUpload.fileName,
        },
      })
      fileToUpload.status = 'completed'
    } catch (error) {
      this.loggerService.error('Error uploading file', error)
      fileToUpload.status = 'failed'
    }
  }

  removeFileFromQueue(fileId: string) {
    this.fileQueue = this.fileQueue.filter((file) => file.id !== fileId)
  }

  get acceptedFileTypesString() {
    return this.acceptedFileTypes.map((fileType) => `.${fileType}`).join(',')
  }

  get acceptedFileTypesDisplay() {
    return this.acceptedFileTypes.map((fileType) => fileType.toUpperCase()).join(', ')
  }
}
